import { ThemeEvents } from "@theme/events";

class ProductInventory extends HTMLElement {
  connectedCallback() {
    this.variantInventoryData = this.readVariantInventoryData();

    this.closestSection = this.closest(
      ".shopify-section, dialog"
    );

    this.closestSection?.addEventListener(
      ThemeEvents.variantUpdate,
      this.updateInventory
    );
  }

  disconnectedCallback() {
    this.closestSection?.removeEventListener(
      ThemeEvents.variantUpdate,
      this.updateInventory
    );
  }

  /**
   * Liest die durch Liquid eingebetteten Bestandsdaten
   * sämtlicher Varianten.
   *
   * @returns {Record<string, Object>}
   */
  readVariantInventoryData() {
    const dataElement = this.querySelector(
      ".tp-variant-inventory-data"
    );

    if (!(dataElement instanceof HTMLScriptElement)) {
      return {};
    }

    try {
      return JSON.parse(dataElement.textContent || "{}");
    } catch (error) {
      console.warn(
        "TacklePeak: Variant inventory data could not be parsed.",
        error
      );

      return {};
    }
  }

  /**
   * Aktualisiert die Anzeige unmittelbar beim Variantenwechsel.
   *
   * @param {CustomEvent} event
   */
  updateInventory = (event) => {
    const detail = event.detail || {};
    const data = detail.data || {};
    const variant = detail.resource;

    /*
     * Wenn über eine Linked Option Group ein anderes Produkt
     * geladen wurde, Produkt-ID und Bestandsdaten aktualisieren.
     */
    if (data.newProduct?.id) {
      this.dataset.productId = String(data.newProduct.id);
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      this.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    /*
     * Status sofort anhand der bereits eingebetteten
     * Variantendaten aktualisieren.
     */
    if (variant?.id) {
      const inventoryVariant =
        this.variantInventoryData[String(variant.id)];

      if (inventoryVariant) {
        this.renderInventoryStatusFromVariant(
          inventoryVariant
        );
      } else if (variant.available === false) {
        this.renderInventoryStatus("out_of_stock");
      } else {
        this.renderInventoryStatus("in_stock");
      }
    } else {
      this.renderInventoryStatus("unavailable");
    }

    /*
     * Neu von Shopify geliefertes HTML anschließend
     * als serverseitige Absicherung übernehmen.
     */
    if (data.html) {
      const newInventory = this.findInventoryInHtml(
        data.html
      );

      if (newInventory) {
        this.renderInventoryFromElement(newInventory);

        const newVariantData =
          this.readVariantInventoryDataFromElement(
            newInventory
          );

        if (Object.keys(newVariantData).length > 0) {
          this.variantInventoryData = newVariantData;
        }
      }
    }
  };

  /**
   * Liest Variantendaten aus einem neu geladenen
   * Inventory-Element.
   *
   * @param {HTMLElement} inventoryElement
   * @returns {Record<string, Object>}
   */
  readVariantInventoryDataFromElement(
    inventoryElement
  ) {
    const dataElement = inventoryElement.querySelector(
      ".tp-variant-inventory-data"
    );

    if (!(dataElement instanceof HTMLScriptElement)) {
      return {};
    }

    try {
      return JSON.parse(dataElement.textContent || "{}");
    } catch (error) {
      console.warn(
        "TacklePeak: New variant inventory data could not be parsed.",
        error
      );

      return {};
    }
  }

  /**
   * Findet das richtige Inventory-Modul im neu
   * gelieferten HTML.
   *
   * @param {Document|HTMLElement} html
   * @returns {HTMLElement|null}
   */
  findInventoryInHtml(html) {
    const productId = this.dataset.productId;

    if (productId) {
      const matchingInventory = html.querySelector(
        `product-inventory[data-product-id="${CSS.escape(
          productId
        )}"]`
      );

      if (matchingInventory instanceof HTMLElement) {
        return matchingInventory;
      }
    }

    const inventory = html.querySelector(
      "product-inventory"
    );

    return inventory instanceof HTMLElement
      ? inventory
      : null;
  }

  /**
   * Übernimmt Status, Texte und eingebettete Daten
   * aus dem serverseitig neu gerenderten Element.
   *
   * @param {HTMLElement} source
   */
  renderInventoryFromElement(source) {
    const sourceStatus = source.querySelector(
      ".product-inventory__status"
    );

    const sourceTitle = source.querySelector(
      ".tp-product-inventory__title"
    );

    const sourceDetail = source.querySelector(
      ".tp-product-inventory__detail"
    );

    const currentStatus = this.querySelector(
      ".product-inventory__status"
    );

    const currentTitle = this.querySelector(
      ".tp-product-inventory__title"
    );

    const currentDetail = this.querySelector(
      ".tp-product-inventory__detail"
    );

    if (!sourceStatus || !currentStatus) {
      return;
    }

    currentStatus.className = sourceStatus.className;

    if (sourceTitle && currentTitle) {
      currentTitle.textContent =
        sourceTitle.textContent?.trim() || "";
    }

    if (sourceDetail && currentDetail) {
      currentDetail.textContent =
        sourceDetail.textContent?.trim() || "";
    }

    for (const [key, value] of Object.entries(
      source.dataset
    )) {
      this.dataset[key] = value;
    }
  }

  /**
   * Bestimmt den Status anhand der exakten
   * Variantendaten.
   *
   * @param {{
   *   available: boolean,
   *   inventory_quantity: number,
   *   inventory_management: string|null,
   *   inventory_policy: string|null
   * }} variant
   */
  renderInventoryStatusFromVariant(variant) {
    const lowStockLimit = Number(
      this.dataset.tpLowStockLimit || 2
    );

    const inventoryQuantity = Number(
      variant.inventory_quantity || 0
    );

    const inventoryManaged =
      variant.inventory_management === "shopify";

    const inventoryPolicy =
      variant.inventory_policy || "deny";

    if (!variant.available) {
      this.renderInventoryStatus("out_of_stock");
      return;
    }

    if (
      inventoryManaged &&
      inventoryPolicy === "deny" &&
      inventoryQuantity > 0 &&
      inventoryQuantity <= lowStockLimit
    ) {
      this.renderInventoryStatus("low");
      return;
    }

    this.renderInventoryStatus("in_stock");
  }

  /**
   * Gibt den jeweiligen Status in der neuen
   * TacklePeak-Infobox aus.
   *
   * @param {"in_stock"|"low"|"out_of_stock"|"unavailable"} status
   */
  renderInventoryStatus(status) {
    const statusElement = this.querySelector(
      ".product-inventory__status"
    );

    const titleElement = this.querySelector(
      ".tp-product-inventory__title"
    );

    const detailElement = this.querySelector(
      ".tp-product-inventory__detail"
    );

    if (
      !statusElement ||
      !titleElement ||
      !detailElement
    ) {
      return;
    }

    let title =
      this.dataset.tpInStockTitle || "Auf Lager";

    let detail =
      this.dataset.tpInStockDetail ||
      "Lieferung in 1–3 Tagen";

    if (status === "low") {
      title =
        this.dataset.tpLowStockTitle ||
        "Nur noch wenige verfügbar";

      detail =
        this.dataset.tpLowStockDetail ||
        "Lieferung in 1–3 Tagen";
    }

    if (status === "out_of_stock") {
      title =
        this.dataset.tpSoldOutTitle ||
        "Variante ausverkauft";

      detail =
        this.dataset.tpSoldOutDetail ||
        "Bitte andere Variante wählen";
    }

    if (status === "unavailable") {
      title =
        this.dataset.tpUnavailableTitle ||
        "Diese Kombination ist nicht erhältlich";

      detail =
        this.dataset.tpUnavailableDetail ||
        "Bitte andere Variante wählen";
    }

    statusElement.classList.remove(
      "tp-product-inventory__status--in_stock",
      "tp-product-inventory__status--low",
      "tp-product-inventory__status--out_of_stock",
      "tp-product-inventory__status--unavailable"
    );

    statusElement.classList.add(
      `tp-product-inventory__status--${status}`
    );

    titleElement.textContent = title;
    detailElement.textContent = detail;
  }
}

if (!customElements.get("product-inventory")) {
  customElements.define(
    "product-inventory",
    ProductInventory
  );
}